import { useState } from "react"
import { Brain, Check, ChevronsUpDown, Lock } from "lucide-react"
import { cn } from "@/lib/utils"
import { Popover, PopoverContent, PopoverTrigger } from "../Popover/Popover"

export type Effort = "low" | "medium" | "high"

export interface EffortOption {
  value: Effort
  label: string
  description: string
  /** Not included in the current plan: shown with a lock, routes to the upgrade flow. */
  locked?: boolean
}

export interface EffortSelectorProps {
  value: Effort
  options: EffortOption[]
  onChange: (value: Effort) => void
  /** Picking a locked tier. Fires instead of onChange, so the selection is untouched. */
  onLockedSelect?: (value: Effort) => void
  className?: string
}

/**
 * Picks how hard the model thinks about the next message. Higher effort means a
 * more capable model, a better answer, and a faster burn of the plan's AI
 * allowance — so the trade-off is spelled out on every option.
 *
 * Tiers a plan doesn't include are still listed rather than hidden: seeing what
 * Medium and High offer is the point of the lock.
 */
export function EffortSelector({
  value,
  options,
  onChange,
  onLockedSelect,
  className,
}: EffortSelectorProps) {
  // Controlled so a selection closes the menu. Items live inside PopoverContent,
  // so Radix's outside-click never fires for them — and a locked pick opens the
  // paywall dialog, which sits below this popover in the stacking order.
  const [open, setOpen] = useState(false)

  const current = options.find((o) => o.value === value) ?? options[0]

  const handleSelect = (option: EffortOption) => {
    setOpen(false)
    if (option.locked) onLockedSelect?.(option.value)
    else onChange(option.value)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={cn(
          "inline-flex items-center gap-1.5 h-6 px-2 rounded-md text-caption text-muted-foreground",
          "hover:bg-hover hover:text-foreground cursor-pointer transition-colors",
          "motion-safe:active:scale-[0.97] motion-safe:transition-transform motion-safe:duration-100",
          className,
        )}
        aria-label={`Reasoning effort: ${current?.label}`}
      >
        <Brain size={13} strokeWidth={1.75} className="shrink-0" />
        {current?.label}
        <ChevronsUpDown size={11} strokeWidth={1.75} className="shrink-0 opacity-60" />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-1">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => handleSelect(option)}
            // Not aria-disabled: a locked row is actionable, it opens the plan picker.
            className={cn(
              "w-full flex items-start gap-2 px-2 py-1.5 rounded-md text-left cursor-pointer",
              "hover:bg-hover transition-colors",
            )}
          >
            <Check
              size={13}
              strokeWidth={2}
              className={cn(
                "shrink-0 mt-0.5",
                option.value === value ? "text-foreground" : "opacity-0",
              )}
            />
            <span className="flex flex-col gap-0.5">
              <span className="flex items-center gap-1.5 text-label text-foreground">
                {option.label}
                {option.locked && (
                  <>
                    <Lock
                      size={11}
                      strokeWidth={2}
                      className="shrink-0 text-warning"
                      aria-hidden="true"
                    />
                    <span className="sr-only">Upgrade required</span>
                  </>
                )}
              </span>
              <span className="text-caption text-muted-foreground">{option.description}</span>
            </span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  )
}
